import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	AfasConnectionListItemModel,
	AfasConnectionSaveModel,
	AfasConnectionSaveResultKind,
	IAfasConnectionSaveResultModel,
	create,
	get,
	update
} from "client/Api/AfasConfiguration/afasConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertDialog,
	Checkbox,
	Dialog,
	Form,
	FormItem,
	FormattedText,
	InputWithMoreInfo,
	SecretValueInput,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { IValueInputAttachable } from "client/Forms/attachable";
import { IObjectValidationStepBuilder, configureValidationStep } from "client/Validation/steps";

enum FormStep {
	Main = 1,
	Otp = 2
}

const validationConfiguration: IValidationConfiguration<AfasConnectionSaveModel> = createValidationConfiguration<AfasConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		})
	);

validationConfiguration.forScalar("environmentKey")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forObject("apiKey")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("useStaticUriForTasks")
	.add(
		isNotNullNorUndefined()
	);

validationConfiguration.forScalar("oneTimePassword")
	.add(
		isNotNullNorUndefined()
	);

const mainValidationStep: IObjectValidationStepBuilder<AfasConnectionSaveModel> = configureValidationStep<AfasConnectionSaveModel>()
	.add(
		"name",
		"serverUrl",
		"environmentKey",
		"apiKey",
		"useStaticUriForTasks"
	);

const otpValidationStep: IObjectValidationStepBuilder<AfasConnectionSaveModel> = configureValidationStep<AfasConnectionSaveModel>()
	.add(
		"oneTimePassword"
	);

const formStepToValidationStep: { [key in FormStep]: IObjectValidationStepBuilder<AfasConnectionSaveModel> } = {
	[FormStep.Main]: mainValidationStep,
	[FormStep.Otp]: otpValidationStep
};

export type AfasConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: AfasConnectionListItemModel): void;
	onClose(): void;
};

const initailModel: AfasConnectionSaveModel = {
	name: "",
	serverUrl: "",
	environmentKey: "",
	apiKey: {
		containsNewValue: true,
		value: ""
	},
	useStaticUriForTasks: false,
	oneTimePassword: {
		isSet: false
	}
};

export const AfasConnectionDialog = (props: AfasConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<AfasConnectionSaveModel>(initailModel);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [isSharedGroupDialogVisible, setIsSharedGroupDialogVisible] = useState<boolean>(false);
	const [originalModel, setOriginalModel] = useState<AfasConnectionSaveModel>(initailModel);
	const [activeStep, setActiveStep] = useState<FormStep>(FormStep.Main);
	const [hasServerError, setHasServerError] = useState<boolean>(false);

	const formHandler: IFormHandler<AfasConnectionSaveModel> = useFormHandler<AfasConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						serverUrl,
						environmentKey,
						useStaticUriForTasks

					} = await get(
							connectionId,
							signal
						);

					const newModel: AfasConnectionSaveModel = {
						name,
						serverUrl,
						environmentKey,
						apiKey: {
							containsNewValue: false,
							value: null
						},
						useStaticUriForTasks,
						oneTimePassword: {
							isSet: false
						}
					};

					setModel(newModel);
					setOriginalModel(newModel);

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			onClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
	} else {
		titleKey = "_EditNameConnection";
	}

	useEffect(
		() => {
			if (
				formHandler.validation
					.getValidationState()
					.kind !== InputValidationResultKind.Invalid
						&& hasServerError
			) {
				const hasError = formHandler.validation
					.getValidationStepStates(formStepToValidationStep[FormStep.Main])
					.some(x => x.kind === InputValidationResultKind.Invalid);

				if (hasError) {
					setActiveStep(FormStep.Main);
					setHasServerError(false);
					return;
				}
			}
		},
		[
			activeStep,
			formHandler.validation,
			hasServerError
		]
	);

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		const stepValidationResult: InputValidationResult = await formHandler.validation
			.validateStep(formStepToValidationStep[activeStep]);

		if (stepValidationResult.kind === InputValidationResultKind.Invalid) {
			setIsBusy(false);
			return;
		}

		const id: number | undefined = connectionId;
		let outcome: IAfasConnectionSaveResultModel;
		try {
			if (id === undefined) {
				outcome = await create(model, globalCancellation);
			} else {
				outcome = await update(id, model, globalCancellation);
			}
			setIsBusy(false);

			switch(outcome.kind) {
				case AfasConnectionSaveResultKind.NewConnectionCreated:
					alertManager.showSuccess(localize("_SuccessfullySavedTheConnection"));
					break;
				case AfasConnectionSaveResultKind.ConnectionUpdated:
					if (!connectionId) {
						throw new Error("Afas connection with unknown id was updated");
					} else {
						alertManager.showSuccess(localize("_SuccessfullySavedTheConnection"));
					}
					break;
				case AfasConnectionSaveResultKind.RequireOneTimePassword:
					setActiveStep(FormStep.Otp);
					setModel({...model, oneTimePassword: {isSet: true, value: ""}});
					break;
				case AfasConnectionSaveResultKind.UnableToValidateConnection:
					setActiveStep(FormStep.Main);
					alertManager.showError(localize("_UnableToValidateConnection"));
					break;
				case AfasConnectionSaveResultKind.RequireEmailAddress:
					setActiveStep(FormStep.Main);
					alertManager.showError(localize("_AfasEmailRequiredToActivateMessageForAdmin"));
					break;
				default:
					throw new UnreachableCaseError(outcome);
			}

			if(outcome.kind === AfasConnectionSaveResultKind.NewConnectionCreated || outcome.kind === AfasConnectionSaveResultKind.ConnectionUpdated) {
				let newConnectionId: number | undefined = id;
				if(outcome.kind === AfasConnectionSaveResultKind.NewConnectionCreated) {
					newConnectionId = outcome.newConnectionId;
				}
				if(newConnectionId === undefined) {
					return;
				}

				onClose();
				onAfterSave({
					id: newConnectionId,
					name: model.name,
					serverUrl: model.serverUrl
				});
			}
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setHasServerError(true);
			setIsBusy(false);
		}
	};

	const enforceToSupplyNewApiKey = (): void => {
		const apiKeyAttachable: IValueInputAttachable<SecretValue> =
			formHandler.for("apiKey");

		const { containsNewValue } = apiKeyAttachable.value;
		if (containsNewValue) {
			return;
		}

		apiKeyAttachable.reducer
			.setValue({
				containsNewValue: true,
				value: ""
			});
	};

	const isNextStepExpected = useMemo(
		() => {
			if(!originalModel) {
				return true;
			}

			return formHandler.value.serverUrl !== originalModel.serverUrl
				|| formHandler.value.environmentKey !== originalModel.environmentKey
				|| formHandler.value.apiKey.containsNewValue;
		},
		[
			formHandler.value.apiKey.containsNewValue,
			formHandler.value.environmentKey,
			formHandler.value.serverUrl,
			originalModel
		]
	);

	return (
		<>
			<Dialog hasBusyIndicator={isBusy} trackingId="afas-connection-dialog">
				<Dialog.Header
					title={
						activeStep === FormStep.Main ? (
							<FormattedText
								text={localize(titleKey)}
								thingsToReplace={[
									{ what: "{{Name}}", filler: localize("_Afas")}
								]}
							/>
						) : (
							localize("_EnterActivationCode")
						)
					}
				/>
				<Dialog.Body>
					{ activeStep === FormStep.Otp && (
						<p>{localize("_AfasActivationCodeIsSentToEmailHint")}</p>
					)}
					<Form onSubmit={submitHandler}>
						{ activeStep === FormStep.Main && (
							<>
								<FormItem label={localize("_Name")}>
									<TextInput {...formHandler.for("name").attachToTextInput()} />
									<ValidationError {...formHandler.for("name").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_ApiUrl")}>
									<TextInput
										{
											...formHandler.for("serverUrl")
												.attachToTextInput()
												.withOnChange(()=> {
													enforceToSupplyNewApiKey();
												})
										}
									/>
									<ValidationError {...formHandler.for("serverUrl").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_EnvironmentKey")}>
									<TextInput
										{
											...formHandler.for("environmentKey")
												.attachToTextInput()
												.withOnChange(()=> {
													enforceToSupplyNewApiKey();
												})
										}
									/>
									<ValidationError {...formHandler.for("environmentKey").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_ApiKey")}>
									<SecretValueInput
										{...formHandler.for("apiKey")
											.attachToSecretValueInput()
										}
										containsInitialValue={!isNewConnection}
									/>
									<ValidationError {...formHandler.for("apiKey").attachToValidationError()} />
								</FormItem>

								<FormItem>
									<InputWithMoreInfo
										onMoreInfo={() => setIsSharedGroupDialogVisible(true)}
										trackingId="shared-group-info-button"
									>
										<Checkbox
											{
												...formHandler
													.for("useStaticUriForTasks")
													.attachToCheckbox()
											}
											text={localize("_AfasUseDefaultTasksUriWhenClicking")}
											ellipseText={false}
										/>
										{
											isSharedGroupDialogVisible && (
												<AlertDialog
													title={localize("_HowDoesThisSettingWork")}
													onClose={() => setIsSharedGroupDialogVisible(false)}
													trackingId="afas-setting-dialog"
													showCloseButton
												>
													{localize("_AfasUseDefaultTasksHintIf404")}
												</AlertDialog>
											)
										}
										<ValidationError {...formHandler.for("useStaticUriForTasks").attachToValidationError()} />
									</InputWithMoreInfo>
								</FormItem>
							</>
						)}
						{
							activeStep === FormStep.Otp && (
								formHandler.for("oneTimePassword").whenType(
									(x): x is SetOptionalModel<string> => x.isSet,
									(x) => (
										<FormItem label={localize("_ActivationCode")}>
											<TextInput {...x.for("value").attachToTextInput()} />
											<ValidationError {...formHandler.for("oneTimePassword").attachToValidationError()} />
										</FormItem>
									)
								)
							)
						}
					</Form>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={
								activeStep === FormStep.Main ? (
									isNextStepExpected ? localize("_Next") : localize("_Save")
								) : (
									localize("_Activate")
								)
							}
							onClick={submitHandler}
							trackingId="save"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
		</>
	);
};
AfasConnectionDialog.displayAfasConnectionDialog = "AfasConnectionDialog";